import { useEffect, useState } from 'react';
import { VideoJsPlayer } from 'video.js';
import { __t } from '../../../i18n';
import { IconVolumeOff } from '../../../icons';
import { ActionBarBtn, UnmuteNoticeWrapper, UnmuteScreenDetector } from '../styles';
import { AUTOPLAY_SUCCESS, VOLUME_CHANGE_EVENTS } from '../PlayerServices';

// Playback starts muted only when the browser refused to autoplay with sound and video.js
// retried muted (`autoplay: 'any'` in PlayerConfigs), so the viewer never asked for it: ask
// them for the click browsers require before the sound can be turned on.
export default function UnmuteNotice({ player }: { player: VideoJsPlayer }) {
  const [showNotice, setShowNotice] = useState<boolean>(false);

  useEffect(() => {
    // 'autoplay-success' is how video.js reports that its autoplay attempt started playback,
    // so a player muted at that point is a player that was not allowed to start with sound.
    // The muted state is read from the player rather than from a 'playing' event because
    // contrib-ads renames the events of the video element while an ad plays, and the attempt
    // can settle before this mounts - hence the initial read for a player already playing.
    const handleAutoplayOutcome = () => setShowNotice(player.muted());
    const handleVolumeChange = () => {
      if (!player.muted()) setShowNotice(false);
    };

    if (!player.paused()) handleAutoplayOutcome();
    player.one(AUTOPLAY_SUCCESS, handleAutoplayOutcome);
    player.on(VOLUME_CHANGE_EVENTS, handleVolumeChange);

    return () => {
      player.off(AUTOPLAY_SUCCESS, handleAutoplayOutcome);
      player.off(VOLUME_CHANGE_EVENTS, handleVolumeChange);
    };
  }, [player]);

  const handleUnmute = () => {
    player.muted(false);
    if (!player.volume()) player.volume(1);
  };

  if (!showNotice) return null;

  return (
    <>
      <UnmuteScreenDetector onClick={handleUnmute} />
      <UnmuteNoticeWrapper>
        <ActionBarBtn onClick={handleUnmute}>
          {__t('player.unmute_notice')}
          <IconVolumeOff />
        </ActionBarBtn>
      </UnmuteNoticeWrapper>
    </>
  );
}
